import React, { useState } from 'react';
import FormWrapper from '../../../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import FileUpload from '../../../../../../Components/Base/FileUpload/FileUpload';
import FormControl from '../../../../../../Components/Form/FormControl';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import CheckboxField from '../../../../../../Components/Form/components/CheckboxField';
import SubmitButton from '../../../../../../Components/Button/SubmitButton';
import RemoveButton from '../../../../../../Components/Button/RemoveButton';
// @ts-ignore
import styles from './ManagerCertificateUploadWizardFileUpload.module.scss';
import InputWrapper from '../../../../../../Components/Form/components/InputWrapper/InputWrapper';
import LinkButton from '../../../../../../Components/Button/LinkButton';
import { Theme } from '../../../../../../Enums/Theme.enum';
import apiClient from '../../../../../../Services/ApiClient';
import { useManagerContext } from '../../../../Services/ManagerContext';
import useTranslation from '../../../../../../Hooks/useTranslation';
import useNotification from '../../../../../../Hooks/useNotification.hook';
import useApiClient from '../../../../../Base/Hooks/useApiClient';
import useRestApiClient from '../../../../../Base/Hooks/useRestApiClient';
import CommercialCertificateHintModal from '../../../../../Certificates/Components/CommercialCertificateHint/CommercialCertificateHintModal';
import { WorkplaceCertificateType } from '../../../../../../graphql/graphql';
import { CertificateType } from '../../../../Enums/CertificateType.enum';
import Loading from '../../../../../../Components/Loading';
import Text from '../../../../../../Components/Base/Text/Text';
import SUKLCertificateHintModal from '../../../../../Certificates/Components/SUKLCertificateHint/SUKLCertificateHintModal';

interface Props {
	onSuccess: (isPasswordLess: boolean, newCertificateId: string) => void;
	onDelete: () => void;
	certificateType: WorkplaceCertificateType;
	certificateId?: string;
}

const TYPE_MAPPER: Record<WorkplaceCertificateType, CertificateType> = {
	[WorkplaceCertificateType.Commercial]: CertificateType.COMMERCIAL,
	[WorkplaceCertificateType.Sukl]: CertificateType.SUKL,
};

const ManagerCertificateUploadWizardFileUpload: React.FC<Props> = ({
	onSuccess,
	certificateType,
	certificateId,
	onDelete,
}) => {
	const { rootWorkplaceId } = useManagerContext();
	const [isLoading, setIsLoading] = useState(false);
	const [selectedFile, setSelectedFile] = useState(null);
	const { t } = useTranslation();
	const { showNotificationSuccess } = useNotification();
	const { displayError } = useApiClient();
	const { showUnexpectedError } = useRestApiClient();
	const [isCommercialCertificateHintOpen, setIsCommercialCertificateHintOpen] =
		useState(false);
	const [isSUKLCertificateHintOpen, setIsSUKLCertificateHintOpen] =
		useState(false);

	const validationSchema = yup.object({
		certificate: yup
			.mixed()
			.required(
				t('validation.managerCertificateUploadWizardFileUpload.certificate'),
			),
		isCertificatePasswordLess: yup.bool(),
	});

	const formMethods = useForm({ resolver: yupResolver(validationSchema) });

	const handleChangeFile = (event: any) => {
		setSelectedFile(event.target.files && event.target.files[0]);
	};

	const handleHintByType = () => {
		if (certificateType === WorkplaceCertificateType.Commercial) {
			setIsCommercialCertificateHintOpen(true);
		} else if (certificateType === WorkplaceCertificateType.Sukl) {
			setIsSUKLCertificateHintOpen(true);
		}
	};

	const handleRemove = async () => {
		if (certificateId) {
			try {
				const deleteResponse = await apiClient.delete(
					`/workplaces/${rootWorkplaceId}/certificate/${certificateId}`,
				);
				if (deleteResponse.status === 200) {
					showNotificationSuccess({
						content: t('component.certificates.certificateRemoved'),
					});
					onDelete();
				} else {
					showUnexpectedError();
				}
			} catch (e) {
				displayError(e);
			}
		}
	};

	const handleSubmit = async (values: any) => {
		setIsLoading(true);
		if (certificateId) {
			try {
				const deleteResponse = await apiClient.delete(
					`/workplaces/${rootWorkplaceId}/certificate/${certificateId}`,
				);
				if (deleteResponse.status !== 200) {
					showUnexpectedError();
					return;
				}
			} catch (e) {
				displayError(e);
				return;
			}
		}

		try {
			const createResponse = await apiClient.post(
				`/workplaces/${rootWorkplaceId}/certificate`,
				{
					type: TYPE_MAPPER[certificateType],
					isCertificatePasswordless: values.isCertificatePasswordLess,
				},
			);
			if (createResponse.status === 200) {
				const newCertificateId = createResponse?.data?.id;
				// @ts-ignore
				const formData = new FormData();
				formData.append('files[]', selectedFile);
				const response = await apiClient.postMultipart(
					`/workplaces/${rootWorkplaceId}/certificate/${newCertificateId}`,
					formData,
				);
				if (response.status === 200) {
					onSuccess(values.isCertificatePasswordLess, newCertificateId);
				} else {
					showUnexpectedError();
				}
			} else {
				showUnexpectedError();
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	if (isLoading) {
		return <Loading />;
	}

	return (
		<>
			{certificateType === WorkplaceCertificateType.Sukl && (
				<>
					<Text
						tx='component.certificates.noSUKLtext'
						className={styles.text}
					/>
					<hr />
				</>
			)}
			<FormWrapper formMethods={formMethods}>
				<InputWrapper>
					<FormControl
						label='component.certificates.selectCertificateFile'
						name='certificate'
						onChange={handleChangeFile}
						isRequired>
						<FileUpload />
					</FormControl>
				</InputWrapper>
				<div className={styles.checkboxWrapper}>
					<CheckboxField
						name='isCertificatePasswordLess'
						tx='component.certificates.isCertificatePasswordless'
						firstUpper
						isWrapped
					/>
				</div>
				<div className={styles.buttons}>
					<div className={styles.leftPart}>
						<SubmitButton
							onSubmit={handleSubmit}
							title='base.shared.continue'
						/>
						{certificateId && (
							<RemoveButton
								onClick={handleRemove}
								title='base.shared.remove'
								firstUpper
								disableFlashMessage
							/>
						)}
					</div>
					<LinkButton
						title={
							certificateType === WorkplaceCertificateType.Commercial
								? 'component.certificates.commerce.step1.hintTitle'
								: 'component.certificates.whatIsSUKL'
						}
						theme={Theme.BLUE}
						onClick={handleHintByType}
					/>
				</div>
			</FormWrapper>
			{isCommercialCertificateHintOpen && (
				<CommercialCertificateHintModal
					isOpen
					onClose={() => setIsCommercialCertificateHintOpen(false)}
				/>
			)}
			{isSUKLCertificateHintOpen && (
				<SUKLCertificateHintModal
					isOpen
					onClose={() => setIsSUKLCertificateHintOpen(false)}
				/>
			)}
		</>
	);
};

export default ManagerCertificateUploadWizardFileUpload;
